import type { ResourceCallbacks } from 'react-admin';
import type { JsonApiResource } from '../index';
import { get, set } from 'lodash';
import { isImageFormData } from '../utils';

export default function createResourceImageLifecycleCallbacks<T extends JsonApiResource>(
  resource: T['type'],
  attributeName: keyof T['attributes'],
): ResourceCallbacks<T> {
  return {
    resource,
    beforeSave: beforeSave<T>(attributeName),
  };
}

function beforeSave<T extends JsonApiResource>(attributeName: keyof T['attributes']) {
  return async (data: T): Promise<T> => {
    const value = get(data, ['attributes', String(attributeName)]);

    if (!isImageFormData(value)) return data;

    if (!(value.rawFile instanceof File)) {
      set(data, `attributes.${String(attributeName)}`, value.src);
      return data;
    }

    set(data, `attributes.${String(attributeName)}`, await readFileAsDataUrl(value.rawFile));

    return data;
  };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Failed to read file as data URL'));
    };

    reader.onerror = () => {
      reject(new Error('Error reading file: ' + reader.error));
    };

    reader.readAsDataURL(file);
  });
}
